import {useTranslation} from 'react-i18next';
import {Grid, Radio, RadioGroup, Stack, Typography} from '@mui/material';
import {Modal} from '../../../../../components/modal/modal';
import {translations} from '../../../../../i18n/translations';
import {useEffect, useState} from 'react';
import FlatInput from '../../../../../components/flat-input/flat-input';
import {useAuth} from '../../../../../contexts/auth/context';
import {EntityOccurrencesRepository} from '../../../../../repositories/entity-occurrences';
import {useParams} from 'react-router-dom';
import {useSnackBar} from '../../../../../contexts/snack-bar/context';
import {RadioLabel} from '../../../../../components/shared/forms/radio';
import {BsCheckAll} from 'react-icons/bs';
import {useEntity} from '../../../../../contexts/entity/context';
import {
  getHandleOccurrenceStep,
  HandleOccurrenceStep,
  TransformedOccurrence,
} from '../../../../../utils/data/occurrences';
import {HandleOccurrenceCard} from './handle-card';
import {OccurrenceHistory} from '../../../../../components/occurrences/occurrences-history/occurrence-history';
import {AffiliatedUser} from '../../../../../backend/responses/affiliated-user';
import {ViewAttachments} from '../../../../../components/daily-care-log/attachments/view-attachments';
import {
  AddAttachmentsForm,
  SavingAttachment,
} from '../../../../../components/daily-care-log/attachments/add-attachments-form';

type HandleOccurrenceModalProps = {
  canEdit: boolean;
  submitting: boolean;
  data: TransformedOccurrence;
  professionals?: AffiliatedUser[];
  onClose: () => void;
  onAcknowledge: () => void;
  onHandle: (
    id: string,
    handled: boolean,
    notes?: string,
    attachments?: SavingAttachment[]
  ) => void;
  onSaveNotes: (id: string, notes: string) => void;
};

export default function HandleOccurrenceModal({
  canEdit,
  submitting,
  data,
  professionals,
  onClose,
  onHandle,
  onSaveNotes,
  onAcknowledge,
}: HandleOccurrenceModalProps) {
  const {t} = useTranslation();
  const {state: authState} = useAuth();
  const snackBarContext = useSnackBar();
  const {updateEntityOccurrencesCount} = useEntity();
  const {entityId}: {entityId: string} = useParams();
  const [step, setStep] = useState<HandleOccurrenceStep>();
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(submitting);
  const [handled, setHandled] = useState<boolean | undefined>(data.markedAsHandled);
  const [isInputsDisabled, setIsInputsDisabled] = useState<boolean>(
    (data.markedAsHandled !== undefined && data.actionRequired) || !canEdit || false
  );
  const [acknowledge, setAcknowledge] = useState(
    data.acknowledgements.find((ack) => ack.userAuthorId === authState.session?.currentUser.id)
  );
  const [attachments, setAttachments] = useState<SavingAttachment[]>();
  const [errors, setErrors] = useState<boolean>(false);

  useEffect(() => {
    const acknowledgeFind = data.acknowledgements.find(
      (ack) => ack.userAuthorId === authState.session?.currentUser.id
    );
    setAcknowledge(acknowledgeFind);
  }, [authState?.session?.currentUser.id, data.acknowledgements]);

  useEffect(() => {
    setStep(getHandleOccurrenceStep(data, authState.session?.currentUser.id));
  }, [authState.session?.currentUser.id, data]);

  const handleAcknowledge = async () => {
    try {
      await EntityOccurrencesRepository.instance.acknowledgeOccurrence(
        entityId,
        data.id,
        notes || undefined
      );

      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.office.professional_caregiver.occurrences.acknowledge_success),
      });
      onAcknowledge();

      setIsSubmitting(false);

      setStep(getHandleOccurrenceStep(data, authState.session?.currentUser.id, true));

      updateEntityOccurrencesCount();
    } catch (error) {
      console.error(error);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.office.professional_caregiver.occurrences.acknowledge_error),
      });
      setIsSubmitting(false);
      setStep('handle');
    }
  };

  return (
    <Modal
      open
      maxWidth="sm"
      fullWidth
      preventCloseClick
      onClose={onClose}
      className="occurrences-handle-modal"
      actions={[
        {
          label: t(translations.modal.cancel),
          type: 'destructive',
          onClick: onClose,
          disabled: isSubmitting,
        },
        {
          label:
            step === 'handled-edit'
              ? t(translations.care_and_activities.handle_task_modal.edit_entry)
              : step === 'acknowledge'
                ? t(translations.office.professional_caregiver.occurrences.acknowledge)
                : t(translations.modal.save),
          type: 'nondestructive',
          variant:
            step === 'handled-edit' && acknowledge?.acknowledgedAt && data.markedAsHandled
              ? 'text'
              : 'contained',
          onClick: () => {
            if (step === 'acknowledge') {
              setIsSubmitting(true);
              handleAcknowledge();
            } else if (step === 'handle' && handled !== undefined) {
              setIsSubmitting(true);
              onHandle(data.id, handled, notes || undefined, attachments);
            } else if (step === 'handled-edit' && data.markedAsHandled !== undefined) {
              setIsInputsDisabled(false);
              setStep('handle');
            } else if (step === 'insert-notes-only') {
              if (notes) {
                onSaveNotes(data.id, notes);
              } else {
                onClose();
              }
            }
          },
          disabled:
            isSubmitting ||
            (step === 'handle' && handled === undefined) ||
            (step === 'insert-notes-only' && !notes) ||
            !canEdit ||
            errors,
          loading: isSubmitting,
        },
      ]}
    >
      <Stack gap="16px">
        <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography className="title">
            {data.type !== '-'
              ? `${t(translations.office.professional_caregiver.occurrences.details)} - ${data.type}`
              : t(translations.office.professional_caregiver.occurrences.details)}
          </Typography>
          {acknowledge?.acknowledgedAt && (
            <Stack gap="2px" flexDirection="row" className="acknowledge-badge" alignItems="center">
              <BsCheckAll />
              <Typography>
                {t(translations.office.professional_caregiver.occurrences.took_acknowledge)}
              </Typography>
            </Stack>
          )}
        </Stack>
        <HandleOccurrenceCard data={data} isLoading={false} />
        {data.actionRequired && (step === 'handle' || step === 'handled-edit') && (
          <RadioGroup row>
            <Grid container columnSpacing={2} rowSpacing={4}>
              <Grid item xs={6}>
                <RadioLabel
                  value="done"
                  disabled={isInputsDisabled}
                  control={<Radio />}
                  label={t(translations.office.professional_caregiver.occurrences.status.handled)}
                  checked={handled === true}
                  onChange={(event, checked) => {
                    if (checked) {
                      setHandled(true);
                    } else {
                      setHandled(false);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <RadioLabel
                  value="not-done"
                  disabled={isInputsDisabled}
                  control={<Radio />}
                  label={t(
                    translations.office.professional_caregiver.occurrences.status.not_handled
                  )}
                  checked={handled === false}
                  onChange={(event, checked) => {
                    if (checked) {
                      setHandled(false);
                    } else {
                      setHandled(true);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </RadioGroup>
        )}
        {(step === 'handle' || step === 'handled-edit' || step === 'insert-notes-only') && (
          <FlatInput
            disabled={isInputsDisabled}
            label={t(translations.office.professional_caregiver.occurrences.add_notes)}
            placeholder={t(translations.office.professional_caregiver.occurrences.write_notes)}
            value={notes}
            multiline
            rows="3"
            onChange={(event) => setNotes(event.target.value)}
          />
        )}
        {data.attachments && data.attachments.length > 0 && isInputsDisabled && (
          <ViewAttachments attachments={data.attachments} />
        )}
        {!isInputsDisabled &&
          (step === 'handle' || step === 'handled-edit' || step === 'insert-notes-only') && (
            <AddAttachmentsForm
              type="view"
              attachmentsUrls={data.attachments}
              onAttachmentsChange={(value) => setAttachments(value)}
              onError={(errors) => {
                if (errors.length > 0) {
                  setErrors(true);
                } else {
                  setErrors(false);
                }
              }}
            />
          )}
        {data.history && data.history.length > 0 && (
          <OccurrenceHistory history={data.history} professionals={professionals} />
        )}
      </Stack>
    </Modal>
  );
}
